import type { VideoFitType } from '@lighthouse/core'
import React, { Suspense, useMemo, useRef } from 'react'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'

import { useApplicationContext, usePageActive } from '../../contexts'
import { getVideoSource } from '../../utils'
import { AliPlayer } from './AliPlayer'
import type { AliPlayerConfig, PlayerRef } from './AliPlayer/types'
import type { MaxQuality } from './BasePlayer'
import { BiliPlayer } from './BiliPlayer'
import { ByecodePlayer } from './ByecodePlayer'
import { ratioReg } from './constants'
import { YoukuPlayer } from './YoukuPlayer'
import { YoutubePlayer } from './YoutubePlayer'
const BasePlayer = React.lazy(() => import('./BasePlayer').then(module => ({ default: module.BasePlayer })))

interface PlayerProps {
    /** 视频地址 */
    source?: string
    /** 宽度 */
    ratio?: string
    /** 自动播放 */
    autoplay?: boolean
    /** 视频圆角 */
    borderRadius?: string | number
    /** 视频控制器  */
    isController?: boolean
    /** 静音 */
    isMute?: boolean
    /** 循环播放 */
    isLoop?: boolean
    /** 恢复上次播放 */
    resumePlay?: boolean
    /** 布局 */
    fitType?: VideoFitType
    /** 封面 */
    cover?: string
    showLoading?: boolean

    /** 是否是原始地址 */
    isOrigin?: boolean
    /** 流媒体最大清晰度，未传则不限制 */
    maxQuality?: MaxQuality
    /** 样式 */
    style?: React.CSSProperties
}

const SCxWrapper = styled.div<{ ratio: string; borderRadius: string | number }>`
    position: relative;
    width: 100%;
    aspect-ratio: ${({ ratio }) => ratio};
    object-fit: contain;
    overflow: hidden;
    border-radius: ${({ borderRadius }) => borderRadius};
`

type AliyunPlayAuthResponse = {
    code: number
    success: boolean
    msg: string
    content: {
        playAuth: string
    }
}

// const fetchAliyunPlayAuth = function (videoId: string): Promise<AliyunPlayAuthResponse> {
//     return fetch(`/lighthouse/api/v1/oss/getVideoPlayAuth?videoId=${videoId}`, {})
//         .then(response => response.json())
//         .catch(reject => {
//             console.log(JSON.stringify(reject))
//         })
// }

function extractVideoId(url: string) {
    const parts = url.split('/')
    return parts[0]
}

function extractUploadedVideoFileId(source: string) {
    if (!source) {
        return ''
    }

    const safeSource = source.startsWith('http') ? source : `https://placeholder.local/${source.replace(/^\/+/, '')}`

    try {
        const { pathname } = new URL(safeSource)
        const segments = pathname.split('/').filter(Boolean)
        return segments.length >= 2 ? segments.at(-2) ?? '' : ''
    } catch {
        const pathname = source.split('?')[0]?.split('#')[0] ?? ''
        const segments = pathname.split('/').filter(Boolean)
        return segments.length >= 2 ? segments.at(-2) ?? '' : ''
    }
}

export const VideoPlayer: React.FunctionComponent<PlayerProps> = ({
    source = '',
    borderRadius = 0,
    ratio,
    isController,
    isLoop,
    resumePlay,
    isMute,
    autoplay,
    fitType,
    cover,
    showLoading,
    isOrigin,
    maxQuality,
    style,
    ...rest
}) => {
    const aliPlayerRef = useRef<PlayerRef>(null)

    const { fetchVideoCoverUrl, fetchVideoPlayAuth } = useApplicationContext()

    const isPageActive = usePageActive()

    const videoSource = useMemo(() => getVideoSource(source ?? ''), [source])
    const uploadedVideoFileId = useMemo(() => extractUploadedVideoFileId(source), [source])
    const shouldUseUploadedCover = useMemo(
        () => !cover && !!uploadedVideoFileId && (videoSource === 'byecode' || (videoSource === 'other' && !source.startsWith('http'))),
        [cover, source, uploadedVideoFileId, videoSource]
    )

    const { value, loading, retry } = useAsyncRetry(async () => {
        if (videoSource === 'aliyun') {
            const videoId = extractVideoId(source)
            if (!videoId || !fetchVideoPlayAuth) {
                return null
            }
            const playAuth = await fetchVideoPlayAuth(videoId)
            return { playAuth, videoId }
        }
        return null
    }, [videoSource, autoplay, isController, isLoop])

    const { value: fallbackCover } = useAsyncRetry(async () => {
        if (!shouldUseUploadedCover || !fetchVideoCoverUrl) {
            return null
        }

        const data = await fetchVideoCoverUrl(uploadedVideoFileId)
        return data?.exists && data.coverUrl ? data.coverUrl : null
    }, [fetchVideoCoverUrl, shouldUseUploadedCover, uploadedVideoFileId])

    const effectiveCover = cover || fallbackCover || undefined

    const cssRatio = useMemo(() => {
        const playerRatio = ratio && ratioReg.test(ratio) ? ratio : '16:9'
        return playerRatio.replace(':', '/')
    }, [ratio])

    const alipLayerOption: Partial<Omit<AliPlayerConfig, 'source' | 'isLive'>> = useMemo(() => {
        return {
            vid: value?.videoId,
            playauth: value?.playAuth,
            autoplay,
            rePlay: isLoop,
            cover: effectiveCover,
            // videoHeight: finallyHeight,
            controlBarVisibility: isController ? 'always' : 'never'
        }
    }, [autoplay, effectiveCover, isController, isLoop, value?.playAuth, value?.videoId])

    // const finallyHeight = useMemo(() => (fitType === 'fill' ? `${height}px` : '100%'), [fitType, height])

    const videoEle = useMemo(() => {
        if (!isPageActive) {
            if (videoSource === 'bilibili' || videoSource === 'youtube' || videoSource === 'youku') {
                return null
            }
        }
        switch (videoSource) {
            case 'bilibili': {
                return <BiliPlayer source={source} autoplay={autoplay} />
            }
            case 'youtube': {
                return <YoutubePlayer source={source} isController={isController} isLoop={isLoop} autoplay={autoplay} />
            }
            case 'youku': {
                return <YoukuPlayer source={source} />
            }
            case 'aliyun': {
                if (loading) {
                    return null
                }

                return <AliPlayer ref={aliPlayerRef} options={alipLayerOption} />
            }
            case 'byecode': {
                return (
                    <Suspense fallback={null}>
                        <ByecodePlayer
                            source={source}
                            ratio={cssRatio}
                            cover={effectiveCover}
                            isController={isController}
                            isLoop={isLoop}
                            resumePlay={resumePlay}
                            isMute={isMute}
                            isOrigin={isOrigin}
                            maxQuality={maxQuality}
                            // height={finallyHeight}
                            autoplay={autoplay}
                            fitType={fitType}
                            showLoading={showLoading}
                        />
                    </Suspense>
                )
            }
            default: {
                return (
                    <Suspense fallback={null}>
                        <BasePlayer
                            source={source}
                            ratio={cssRatio}
                            cover={effectiveCover}
                            isController={isController}
                            isLoop={isLoop}
                            resumePlay={resumePlay}
                            isMute={isMute}
                            // height={finallyHeight}
                            autoplay={autoplay}
                            fitType={fitType}
                            showLoading={showLoading}
                            maxQuality={maxQuality}
                        />
                    </Suspense>
                )
            }
        }
    }, [
        alipLayerOption,
        autoplay,
        cssRatio,
        effectiveCover,
        fitType,
        isController,
        isLoop,
        isMute,
        isOrigin,
        isPageActive,
        loading,
        maxQuality,
        resumePlay,
        showLoading,
        source,
        videoSource
    ])

    const mergedStyle = useMemo(() => {
        if (!isPageActive) {
            return { ...style, display: 'none' }
        }
        return style
    }, [style, isPageActive])

    return (
        <SCxWrapper ratio={cssRatio} borderRadius={borderRadius} style={mergedStyle} {...rest}>
            {videoEle}
        </SCxWrapper>
    )
}
